import ResponsiveDialog from '@/components/ResponsiveDialog';
import TripInfo, {
  type TripInfoSource,
} from '@/components/manage/change-trips/TripInfo';
import { useChangeBookingSelection } from '@/hooks/useChangeBookingSelection';
import { TransText, type TransTextKeys } from '@/i18n/trans/text';
import type { BookingDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import {
  ALERT_REASON,
  getAncillaryIdFromInstance,
  getAncillaryName,
  type AlertReason,
} from '@/utils/changeTrips';
import {
  AlertBox,
  Button,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import { useEffect, useMemo, type FC } from 'react';
import { useMediaQuery } from 'react-responsive';

interface ChangeTripsModalAlertProps {
  alert?: AlertReason | null;
  booking?: BookingDTO | null;
  isOpen: boolean;
  onClose: () => void;
  onBack?: () => void;
  onContinue?: () => void;
  selectedTrip?: TripInfoSource | null;
}

interface AlertContent {
  titleKey: TransTextKeys;
  alertKey: TransTextKeys;
  descriptionKey: TransTextKeys;
  hintKey?: TransTextKeys;
}

// Record<ALERT_REASON, _> forces a new entry per reason — keeps copy in
// lockstep with the validator surface in `detectConflictReason`.
const ALERT_CONTENT: Record<ALERT_REASON, AlertContent> = {
  [ALERT_REASON.CONFLICTING_TRIPS]: {
    titleKey: 'conflictingTrips',
    alertKey: 'conflictingTrips',
    descriptionKey: 'conflictingTripsDescription',
    hintKey: 'conflictingTripsHint',
  },
  [ALERT_REASON.SELECTED_TICKET_JUST_SOLD_OUT]: {
    titleKey: 'selectedTicketJustSoldOut',
    alertKey: 'selectedTicketJustSoldOut',
    descriptionKey: 'selectedTicketJustSoldOutDescription',
    hintKey: 'selectedTicketJustSoldOutHint',
  },
  [ALERT_REASON.SOLD_OUT]: {
    titleKey: 'noTicketsAvailable',
    alertKey: 'noTicketsAvailable',
    descriptionKey: 'noTicketsAvailableDescription',
  },
  [ALERT_REASON.UPGRADE_BLOCKED_BY_RESTRICTIVE_ANCILLARY]: {
    titleKey: 'upgradeBlockedByRestrictiveAncillary',
    alertKey: 'upgradeBlockedByRestrictiveAncillary',
    descriptionKey: 'upgradeBlockedByRestrictiveAncillaryDescription',
    hintKey: 'upgradeBlockedByRestrictiveAncillaryHint',
  },
};

const ChangeTripsModalAlert: FC<ChangeTripsModalAlertProps> = ({
  alert,
  booking,
  isOpen,
  onBack,
  onClose,
  onContinue,
  selectedTrip,
}) => {
  const { conflictReason, selectedInboundTrip, selectedOutboundTrip } =
    useChangeBookingSelection();
  const isLaptopOrBigger = useMediaQuery({ minWidth: breakpoints.laptop });

  const effectiveAlert = useMemo<AlertReason | null>(
    () =>
      alert ?? (conflictReason ? { code: conflictReason, message: '' } : null),
    [alert, conflictReason]
  );

  // Auto-close if the conflict resolves underneath us (e.g. background selection change).
  useEffect(() => {
    if (isOpen && effectiveAlert === null) {
      onClose();
    }
  }, [effectiveAlert, isOpen, onClose]);

  if (!effectiveAlert) return null;

  const content = ALERT_CONTENT[effectiveAlert.code];
  const ancillaryName = getAncillaryName(
    booking,
    getAncillaryIdFromInstance(effectiveAlert.instance)
  );
  const hintValues = ancillaryName ? { ancillaryName } : undefined;
  const showContinue =
    effectiveAlert.code === ALERT_REASON.SELECTED_TICKET_JUST_SOLD_OUT &&
    Boolean(onContinue);
  const soldOutSelectedTrip =
    effectiveAlert.code === ALERT_REASON.SELECTED_TICKET_JUST_SOLD_OUT ||
    effectiveAlert.code === ALERT_REASON.SOLD_OUT
      ? (selectedTrip ?? selectedOutboundTrip ?? selectedInboundTrip)
      : null;

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={<TransText i18nKey={content.titleKey} />}
      footer={
        <div className="flex w-full gap-2 laptop:flex-row laptop:justify-end">
          <Button
            type="button"
            variant="tertiary"
            onClick={onBack ?? onClose}
            data-testid="change-trips-alert-back"
            size={isLaptopOrBigger ? 'regular' : 'large'}
            className="rounded-lg laptop:w-auto"
            startAdornment={<Icons.arrowLeft />}
          >
            <TransText i18nKey="back" />
          </Button>
          {showContinue && (
            <Button
              type="button"
              onClick={onContinue}
              data-testid="change-trips-alert-continue"
              size={isLaptopOrBigger ? 'regular' : 'large'}
              className="rounded-lg laptop:w-auto"
              startAdornment={<Icons.check />}
            >
              <TransText i18nKey="continue" />
            </Button>
          )}
        </div>
      }
      content={
        <div
          className="flex flex-col gap-4 p-4"
          data-testid="change-trips-alert-modal-content"
        >
          <AlertBox
            variant="negative"
            data-testid="change-trips-alert-box"
            text={<TransText i18nKey={content.alertKey} />}
          />
          <Typography variant="paragraph">
            <TransText i18nKey={content.descriptionKey} />
          </Typography>
          <TripInfo
            trip={soldOutSelectedTrip}
            testId="change-trips-alert-selected-trip"
          />
          {content.hintKey && (
            <Typography variant="body1-bold">
              <TransText i18nKey={content.hintKey} values={hintValues} />
            </Typography>
          )}
        </div>
      }
    />
  );
};

export default ChangeTripsModalAlert;
